import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import DrawingCanvas from '../components/DrawingCanvas';
import MathToolbar from '../components/MathToolbar';
import { ArrowLeft, Clock, Send, CheckCircle, Circle } from 'lucide-react';
import { toast } from 'sonner';

const TakeExam = () => {
  const { examId } = useParams();
  const navigate = useNavigate();
  const [exam, setExam] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [mcqAnswers, setMcqAnswers] = useState({});  // { questionIdx: selectedOptionIdx }
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);
  
  // Refs for textareas (for MathToolbar cursor positioning)
  const textareaRefs = useRef({});

  const fetchExam = async () => {
    try {
      const response = await api.get(`/exams/${examId}`);
      setExam(response.data);
      setTimeRemaining(response.data.duration_minutes * 60);
      
      // Initialize answers for descriptive/structured questions (including sub-parts)
      const initialAnswers = [];
      response.data.questions.forEach((q, qIndex) => {
        if (q.question_type === 'descriptive' || q.question_type === 'structured' || !q.question_type) {
          (q.parts || []).forEach((p, pIndex) => {
            // Check if part has sub-parts (IGCSE structure)
            if (p.sub_parts && p.sub_parts.length > 0) {
              p.sub_parts.forEach((sp, spIndex) => {
                initialAnswers.push({
                  question_idx: qIndex,
                  part_idx: pIndex,
                  sub_part_idx: spIndex,
                  text_answer: '',
                  drawing_data: null,
                });
              });
            } else {
              // Simple part without sub-parts
              initialAnswers.push({
                question_idx: qIndex,
                part_idx: pIndex,
                sub_part_idx: -1,
                text_answer: '',
                drawing_data: null,
              });
            }
          });
        }
      });
      setAnswers(initialAnswers);
    } catch (err) {
      alert('Failed to load exam');
      navigate('/student/dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExam();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [examId]);

  useEffect(() => {
    if (timeRemaining <= 0 || showResults) return;

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeRemaining, showResults]);

  const updateAnswer = (qIndex, pIndex, field, value, subPartIdx = -1) => {
    setAnswers((prev) => {
      const newAnswers = [...prev];
      const answerIndex = newAnswers.findIndex(
        (a) => a.question_idx === qIndex && a.part_idx === pIndex && a.sub_part_idx === subPartIdx
      );
      if (answerIndex !== -1) {
        newAnswers[answerIndex][field] = value;
      }
      return newAnswers;
    });
  };

  const getAnswer = (qIndex, pIndex, subPartIdx = -1) => {
    return answers.find(
      (a) => a.question_idx === qIndex && a.part_idx === pIndex && a.sub_part_idx === subPartIdx
    );
  };

  const selectMCQAnswer = (qIndex, optionIdx) => {
    setMcqAnswers((prev) => ({
      ...prev,
      [qIndex]: optionIdx,
    }));
  };

  const handleSubmit = async () => {
    
    if (submitting) {
      toast.warning('Please wait, submission in progress...', { duration: 2000 });
      return;
    }
    
    // Show loading immediately
    setSubmitting(true);
    
    // Show big toast
    const loadingToast = toast.loading('📤 Submitting your exam...', {
      duration: 10000,
    });
    
    try {
      // Combine descriptive answers with MCQ answers
      const allAnswers = [...answers];
      
      // Add MCQ answers
      Object.entries(mcqAnswers).forEach(([qIdx, selectedOption]) => {
        allAnswers.push({
          question_idx: parseInt(qIdx),
          part_idx: -1,  // MCQ doesn't have parts
          text_answer: null,
          drawing_data: null,
          selected_option: selectedOption,
        });
      });
      
      
      const response = await api.post('/submissions', {
        exam_id: examId,
        answers: allAnswers,
        timer_remaining: timeRemaining,
      });
      
      
      toast.dismiss(loadingToast);
      
      // Check if exam has MCQs and show results
      if (response.data.is_mcq_auto_graded && response.data.mcq_results) {
        setSubmissionResult(response.data);
        setShowResults(true);
        toast.success('✅ Exam submitted! Check your results below.', {
          duration: 3000,
        });
      } else {
        toast.success('✅ EXAM SUBMITTED SUCCESSFULLY!\n\nRedirecting to dashboard...', {
          duration: 3000,
          style: {
            fontSize: '16px',
            fontWeight: 'bold',
          },
        });
        
        setTimeout(() => {
          navigate('/student/dashboard');
        }, 1500);
      }
    } catch (err) {
      console.error('❌ Failed to submit exam:', err);
      console.error('Error details:', err.response?.data);
      
      toast.dismiss(loadingToast);
      toast.error(`❌ Submission Failed!\n\n${err.response?.data?.detail || err.message}`, {
        duration: 6000,
        style: {
          fontSize: '16px',
        },
      });
      setSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-slate-500">Loading exam...</div>
      </div>
    );
  }

  const mainContent = (
    <div className="min-h-screen bg-background">
      {/* Sticky Timer */}
      <div
        data-testid="exam-timer"
        className={`sticky top-0 z-50 px-6 py-3 text-center font-mono text-xl font-bold shadow-md transition-colors ${
          timeRemaining < 300 ? 'bg-red-600 text-white' : 'bg-indigo-900 text-white'
        }`}
      >
        <div className="flex items-center justify-center gap-2">
          <Clock className="w-5 h-5" />
          Time Remaining: {formatTime(timeRemaining)}
        </div>
      </div>

      <div className="max-w-3xl mx-auto py-12 px-6 space-y-12">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">{exam.title}</h1>
          <p className="text-slate-600">By {exam.teacher_name}</p>
        </div>

        {/* Questions */}
        {exam.questions.map((question, qIndex) => (
          <div
            key={qIndex}
            data-testid={`question-${qIndex}`}
            className={`bg-white rounded-xl border-2 p-8 shadow-sm relative ${
              question.question_type === 'mcq' ? 'border-green-200' : 'border-slate-200'
            }`}
          >
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center gap-3">
                <h2 className="text-2xl font-semibold text-slate-900">
                  Question {qIndex + 1}
                </h2>
                {question.question_type === 'mcq' && (
                  <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
                    MCQ
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="px-3 py-1 bg-indigo-100 text-indigo-700 font-medium rounded-full text-sm">
                  {question.marks} marks
                </span>
                {question.question_type === 'mcq' && question.negative_marks > 0 && (
                  <span className="px-3 py-1 bg-red-100 text-red-700 font-medium rounded-full text-sm">
                    -{question.negative_marks} for wrong
                  </span>
                )}
              </div>
            </div>

            <p className="text-lg text-slate-700 mb-6 leading-relaxed">
              {question.question_text}
            </p>

            {/* Question Diagram (MCQ) */}
            {question.question_type === 'mcq' && question.question_diagram && (
              <div className="mb-6">
                <img
                  src={question.question_diagram}
                  alt="Question diagram"
                  className="max-w-full rounded-lg border border-slate-200"
                />
              </div>
            )}

            {/* MCQ Options */}
            {question.question_type === 'mcq' && question.mcq_options && (
              <div className="space-y-3">
                {question.mcq_options.map((option, optIndex) => (
                  <button
                    key={optIndex}
                    type="button"
                    onClick={() => selectMCQAnswer(qIndex, optIndex)}
                    className={`w-full flex items-start gap-4 p-4 rounded-lg border-2 transition-all text-left ${
                      mcqAnswers[qIndex] === optIndex
                        ? 'border-green-500 bg-green-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex-shrink-0 mt-0.5">
                      {mcqAnswers[qIndex] === optIndex ? (
                        <CheckCircle className="w-6 h-6 text-green-600" />
                      ) : (
                        <Circle className="w-6 h-6 text-slate-300" />
                      )}
                    </div>
                    <div className="flex-1">
                      <span className="font-medium text-slate-700">
                        {String.fromCharCode(65 + optIndex)}.
                      </span>
                      {option.option_text && (
                        <span className="ml-2 text-slate-700">{option.option_text}</span>
                      )}
                      {option.option_image && (
                        <img
                          src={option.option_image}
                          alt={`Option ${String.fromCharCode(65 + optIndex)}`}
                          className="mt-2 max-h-32 rounded border border-slate-200"
                        />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Descriptive/Structured Parts */}
            {(question.question_type === 'descriptive' || question.question_type === 'structured' || !question.question_type) && question.parts && (
            <div className="space-y-8">
              {question.parts.map((part, pIndex) => (
                <div
                  key={pIndex}
                  data-testid={`part-${qIndex}-${pIndex}`}
                  className="p-6 bg-slate-50 rounded-lg border border-slate-200"
                >
                  <h3 className="font-semibold text-slate-900 mb-4">
                    Part {String.fromCharCode(97 + pIndex)}) {part.part_text}
                  </h3>

                  {/* Check if part has IGCSE sub-parts */}
                  {part.sub_parts && part.sub_parts.length > 0 ? (
                    // IGCSE Sub-parts Structure
                    <div className="space-y-6 pl-4 border-l-2 border-amber-300">
                      {part.sub_parts.map((subPart, spIndex) => {
                        const answer = getAnswer(qIndex, pIndex, spIndex);
                        const romanNumeral = ['i', 'ii', 'iii', 'iv', 'v', 'vi', 'vii', 'viii'][spIndex] || (spIndex + 1);
                        return (
                          <div key={spIndex} className="p-4 bg-white rounded-lg border border-amber-200">
                            <div className="flex items-start justify-between mb-3">
                              <h4 className="font-medium text-amber-800">
                                {String.fromCharCode(97 + pIndex)}({romanNumeral}) {subPart.sub_part_text}
                              </h4>
                              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded">
                                {subPart.marks} mark{subPart.marks > 1 ? 's' : ''}
                                {subPart.assessment_objective && ` • ${subPart.assessment_objective}`}
                              </span>
                            </div>

                            {/* Sub-part Diagram with Drawing */}
                            {subPart.has_drawing && subPart.diagram_base64 && (
                              <div className="mb-4">
                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                  Draw your answer:
                                </label>
                                <DrawingCanvas
                                  backgroundImage={subPart.diagram_base64}
                                  onSave={(drawingData) =>
                                    updateAnswer(qIndex, pIndex, 'drawing_data', drawingData, spIndex)
                                  }
                                />
                              </div>
                            )}

                            {/* Sub-part Diagram without Drawing */}
                            {!subPart.has_drawing && subPart.diagram_base64 && (
                              <div className="mb-4">
                                <img
                                  src={subPart.diagram_base64}
                                  alt="Question diagram"
                                  className="max-w-full rounded-lg border border-slate-200"
                                />
                              </div>
                            )}

                            {/* Sub-part Text Answer */}
                            <div>
                              <div className="relative">
                                <MathToolbar
                                  textareaRef={{ current: textareaRefs.current[`${qIndex}-${pIndex}-${spIndex}`] }}
                                  value={answer?.text_answer || ''}
                                  onChange={(newValue) =>
                                    updateAnswer(qIndex, pIndex, 'text_answer', newValue, spIndex)
                                  }
                                />
                                <textarea
                                  ref={(el) => { textareaRefs.current[`${qIndex}-${pIndex}-${spIndex}`] = el; }}
                                  data-testid={`answer-text-${qIndex}-${pIndex}-${spIndex}`}
                                  value={answer?.text_answer || ''}
                                  onChange={(e) =>
                                    updateAnswer(qIndex, pIndex, 'text_answer', e.target.value, spIndex)
                                  }
                                  className="w-full px-4 py-3 rounded-b-lg rounded-t-none border border-slate-200 border-t-0 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2"
                                  rows="3"
                                  placeholder="Type your answer here..."
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    // Simple Part Structure (no sub-parts)
                    <>
                      {/* Diagram with Drawing */}
                      {part.has_drawing && part.diagram_base64 && (
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Draw your answer:
                          </label>
                          <DrawingCanvas
                            backgroundImage={part.diagram_base64}
                            onSave={(drawingData) =>
                              updateAnswer(qIndex, pIndex, 'drawing_data', drawingData)
                            }
                          />
                        </div>
                      )}

                      {/* Diagram without Drawing */}
                      {!part.has_drawing && part.diagram_base64 && (
                        <div className="mb-4">
                          <img
                            src={part.diagram_base64}
                            alt="Question diagram"
                            className="max-w-full rounded-lg border border-slate-200"
                          />
                        </div>
                      )}

                      {/* Text Answer with Math Toolbar */}
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Your Answer:
                          {part.marks && <span className="text-slate-500 ml-2">({part.marks} marks)</span>}
                        </label>
                        <div className="relative">
                          <MathToolbar
                            textareaRef={{ current: textareaRefs.current[`${qIndex}-${pIndex}`] }}
                            value={getAnswer(qIndex, pIndex)?.text_answer || ''}
                            onChange={(newValue) =>
                              updateAnswer(qIndex, pIndex, 'text_answer', newValue)
                            }
                          />
                          <textarea
                            ref={(el) => { textareaRefs.current[`${qIndex}-${pIndex}`] = el; }}
                            data-testid={`answer-text-${qIndex}-${pIndex}`}
                            value={getAnswer(qIndex, pIndex)?.text_answer || ''}
                            onChange={(e) =>
                              updateAnswer(qIndex, pIndex, 'text_answer', e.target.value)
                            }
                            className="w-full px-4 py-3 rounded-b-lg rounded-t-none border border-slate-200 border-t-0 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                            rows="4"
                            placeholder="Type your answer here... Use the toolbar above for math symbols"
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
            )}
          </div>
        ))}

        {/* Submit Button - FIXED VERSION */}
        <div className="flex items-center justify-center gap-4 sticky bottom-0 bg-yellow-50 py-6 border-t-4 border-yellow-400 shadow-lg">
          <button
            type="button"
            onClick={() => {
              navigate('/student/dashboard');
            }}
            disabled={submitting}
            className="px-8 py-4 bg-white border-2 border-slate-300 text-slate-700 font-bold text-lg rounded-lg hover:bg-slate-50 transition-all disabled:opacity-50"
          >
            ← Cancel & Go Back
          </button>
          
          {!showConfirmModal ? (
            <button
              type="button"
              data-testid="submit-exam-btn"
              onClick={() => {
                setShowConfirmModal(true);
              }}
              disabled={submitting}
              className="px-12 py-4 bg-green-600 hover:bg-green-700 text-white font-bold text-lg rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-xl"
            >
              {submitting ? '⏳ Submitting...' : '✓ SUBMIT EXAM NOW'}
            </button>
          ) : (
            <div className="px-12 py-4 bg-yellow-500 text-white font-bold text-lg rounded-lg">
              ⚠️ See confirmation below ↓
            </div>
          )}
        </div>

        {/* Custom Confirmation Modal - BIGGER AND MORE VISIBLE */}
        {showConfirmModal && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50"
            style={{ backdropFilter: 'blur(4px)' }}
          >
            <div className="bg-white rounded-2xl p-10 max-w-lg mx-4 shadow-2xl border-4 border-yellow-400 animate-pulse">
              <div className="text-center mb-8">
                <div className="w-24 h-24 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6 border-4 border-yellow-400">
                  <span className="text-6xl">⚠️</span>
                </div>
                <h2 className="text-3xl font-bold text-slate-900 mb-4">
                  Submit Your Exam?
                </h2>
                <p className="text-lg text-slate-600 mb-2">
                  ⚠️ You CANNOT change your answers after submitting!
                </p>
                <p className="text-lg text-slate-600">
                  Are you sure you want to submit now?
                </p>
              </div>
              
              <div className="flex gap-4">
                <button
                  onClick={() => {
                    setShowConfirmModal(false);
                  }}
                  className="flex-1 px-8 py-4 bg-slate-200 text-slate-900 font-bold text-lg rounded-lg hover:bg-slate-300 transition-all"
                >
                  ❌ No, Go Back
                </button>
                <button
                  onClick={() => {
                    setShowConfirmModal(false);
                    handleSubmit();
                  }}
                  className="flex-1 px-8 py-4 bg-green-600 text-white font-bold text-lg rounded-lg hover:bg-green-700 transition-all shadow-lg"
                >
                  ✅ Yes, Submit Now!
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // MCQ Results display
  if (showResults && submissionResult) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100">
        <div className="max-w-3xl mx-auto py-12 px-6">
          {/* Results Header */}
          <div className="bg-white rounded-2xl border-2 border-green-500 p-8 mb-8 text-center shadow-lg">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Exam Submitted!</h1>
            <p className="text-slate-600 mb-6">Here are your MCQ results</p>
            
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-green-50 rounded-lg p-4">
                <p className="text-sm text-slate-600">Score</p>
                <p className="text-3xl font-bold text-green-600">{submissionResult.mcq_score}</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-sm text-slate-600">Total Marks</p>
                <p className="text-3xl font-bold text-slate-700">{submissionResult.mcq_total}</p>
              </div>
              <div className="bg-indigo-50 rounded-lg p-4">
                <p className="text-sm text-slate-600">Percentage</p>
                <p className="text-3xl font-bold text-indigo-600">
                  {submissionResult.mcq_total > 0 
                    ? Math.round((submissionResult.mcq_score / submissionResult.mcq_total) * 100)
                    : 0}%
                </p>
              </div>
            </div>

            <button
              onClick={() => navigate('/student/dashboard')}
              className="px-8 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-all"
            >
              Return to Dashboard
            </button>
          </div>

          {/* Question-wise Results */}
          <h2 className="text-xl font-semibold text-slate-900 mb-4">Question-wise Results</h2>
          <div className="space-y-4">
            {submissionResult.mcq_results?.map((result, idx) => {
              const question = exam?.questions[result.question_idx];
              return (
                <div
                  key={idx}
                  className={`bg-white rounded-xl border-2 p-6 ${
                    result.is_correct ? 'border-green-400' : 'border-red-400'
                  }`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                        result.is_correct ? 'bg-green-500' : 'bg-red-500'
                      }`}>
                        {result.is_correct ? '✓' : '✗'}
                      </span>
                      <h3 className="font-semibold text-slate-900">
                        Question {result.question_idx + 1}
                      </h3>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      result.marks_awarded >= 0 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {result.marks_awarded >= 0 ? '+' : ''}{result.marks_awarded} marks
                    </span>
                  </div>

                  <p className="text-slate-700 mb-4">{question?.question_text}</p>

                  {question?.question_diagram && (
                    <img
                      src={question.question_diagram}
                      alt="Question diagram"
                      className="max-w-full h-32 rounded-lg border border-slate-200 mb-4"
                    />
                  )}

                  <div className="grid grid-cols-2 gap-3">
                    {question?.mcq_options?.map((opt, optIdx) => (
                      <div
                        key={optIdx}
                        className={`p-3 rounded-lg border-2 ${
                          optIdx === result.correct_option
                            ? 'border-green-500 bg-green-50'
                            : optIdx === result.selected_option && !result.is_correct
                            ? 'border-red-500 bg-red-50'
                            : 'border-slate-200'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{String.fromCharCode(65 + optIdx)}.</span>
                          {opt.option_text && <span>{opt.option_text}</span>}
                          {optIdx === result.correct_option && (
                            <span className="text-green-600 text-sm ml-auto">✓ Correct</span>
                          )}
                          {optIdx === result.selected_option && optIdx !== result.correct_option && (
                            <span className="text-red-600 text-sm ml-auto">Your answer</span>
                          )}
                        </div>
                        {opt.option_image && (
                          <img src={opt.option_image} alt="" className="mt-2 h-16 rounded" />
                        )}
                      </div>
                    ))}
                  </div>

                  {result.selected_option === null && (
                    <p className="mt-3 text-amber-600 text-sm">Not answered</p>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-8 text-center">
            <button
              onClick={() => navigate('/student/dashboard')}
              className="px-8 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-all"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return mainContent;
};

export default TakeExam;